import { useCallback, useEffect, useRef, useState } from "react";
import {
  FileWarning, Upload, Camera, Loader2, Copy, RefreshCw, Trash2, CalendarClock,
  ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, Sparkles, FileText,
} from "lucide-react";
import { toast } from "sonner";

function deadlinePillClass(days) {
  if (days <= 14) return "pill-bad";
  if (days <= 30) return "pill-warn";
  return "pill-ok";
}
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";

/**
 * USCIS Notices — upload a photo or scan of any letter USCIS sent you
 * (Receipt, RFE, NOID, Approval, Denial, Biometrics, Interview) and the
 * platform will read it, extract the receipt number and deadline, update
 * the case status automatically, and — for RFEs/NOIDs — draft a plain-English
 * response letter your attorney can review.
 *
 * Mounted on CaseWorkspace above the FilingPacket card.
 */

const TYPE_COLORS = {
  receipt: "pill-ok",
  approval: "pill-ok",
  denial: "pill-bad",
  noid: "pill-bad",
  rfe: "pill-warn",
  biometrics: "pill-ai",
  interview: "pill-ai",
};

const TYPE_LABEL = {
  receipt: "Receipt Notice",
  rfe: "Request for Evidence (RFE)",
  noid: "Notice of Intent to Deny (NOID)",
  approval: "Approval Notice",
  denial: "Denial",
  biometrics: "Biometrics Appointment",
  interview: "Interview Notice",
  transfer: "Case Transfer",
  reopening: "Reopening",
  withdrawal: "Withdrawal",
  fee_receipt: "Fee Receipt",
  other: "Other Notice",
};

export default function NoticesCard({ caseData }) {
  const caseId = caseData.id;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/notices`);
      setRows(data);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't load your USCIS notices."));
    } finally {
      setLoading(false);
    }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    toast.loading(`Reading ${file.name}…`, { id: "notice-upload" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/cases/${caseId}/notices`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const type = data.extracted?.notice_type;
      const label = TYPE_LABEL[type] || "notice";
      toast.success(`${label} read successfully — scroll down to review.`, { id: "notice-upload" });
      setRows((r) => [data, ...r]);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't read that notice. Try again with a clearer scan."),
        { id: "notice-upload" });
    } finally {
      setUploading(false);
    }
  };

  const remove = async (nid) => {
     
    if (!window.confirm("Remove this notice from the case?")) return;
    try {
      await api.delete(`/cases/${caseId}/notices/${nid}`);
      setRows((r) => r.filter((row) => row.id !== nid));
      toast.success("Notice removed.");
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  return (
    <div className="card-surface p-6 mb-5" data-testid="notices-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}>
          <FileWarning className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
            USCIS Notices &amp; Responses
          </h3>
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Snap a photo (or upload a scan) of any letter USCIS sent you — Receipt,
            RFE, NOID, Approval, Denial, Biometrics, or Interview. We'll read it,
            update your case status, capture the deadline, and — for RFEs and NOIDs —
            prepare a plain-English draft response for your attorney to review.
          </p>
        </div>
      </div>

      {/* Upload zone */}
      <div
        className="dashed-drop p-5 text-center cursor-pointer"
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (uploading) return;
          const f = e.dataTransfer.files?.[0];
          if (f) upload(f);
        }}
        data-testid="notices-dropzone"
      >
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="notices-file-input"
        />
        {/* Hidden mobile-camera-capture input — triggered by the "Take a photo" button */}
        <input
          ref={cameraRef}
          type="file"
          className="hidden"
          accept="image/*"
          capture="environment"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="notices-camera-input"
        />
        {uploading ? (
          <div className="py-2">
            <div className="dot-typing mb-2"><span></span><span></span><span></span></div>
            <div className="font-semibold" style={{ color: "var(--text-primary)" }}>
              Reading the notice…
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              This usually takes 10–20 seconds.
            </div>
          </div>
        ) : (
          <>
            <Upload className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--action)" }} />
            <div className="font-semibold" style={{ color: "var(--text-primary)" }}>
              Drop a USCIS notice here, or click to choose a file
            </div>
            <div className="text-xs mt-1 mb-3" style={{ color: "var(--text-secondary)" }}>
              PDF, JPG, PNG, or WEBP · Max 8 MB
            </div>
            <button
              type="button"
              onClick={(ev) => { ev.stopPropagation(); cameraRef.current?.click(); }}
              className="btn-primary inline-flex items-center gap-2 text-sm px-4 py-2"
              data-testid="notices-camera-btn"
            >
              <Camera className="w-4 h-4" />
              Take a photo with my phone
            </button>
          </>
        )}
      </div>

      {loading ? (
        <div className="mt-4 text-sm flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your notices…
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-4 text-xs italic" style={{ color: "var(--text-secondary)" }}>
          No notices uploaded yet. Any letters USCIS sends you can be dropped here — it
          keeps everything in one place and gives you a draft to respond with.
        </div>
      ) : (
        <ul className="mt-5 space-y-3" data-testid="notices-list">
          {rows.map((n) => (
            <NoticeRow key={n.id} caseId={caseId} notice={n}
                       onDelete={() => remove(n.id)}
                       onUpdate={(next) => setRows((r) => r.map((x) => x.id === next.id ? next : x))} />
          ))}
        </ul>
      )}
    </div>
  );
}

function NoticeRow({ caseId, notice, onDelete, onUpdate }) {
  const [open, setOpen] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [instruction, setInstruction] = useState("");
  const e = notice.extracted || {};
  const type = (e.notice_type || "other").toLowerCase();
  const pillClass = TYPE_COLORS[type] || "pill-ai";
  const deadline = e.response_deadline;
  const showDraft = type === "rfe" || type === "noid";
  const daysUntil = deadline ? daysBetween(new Date(), new Date(deadline)) : null;

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(
        `/cases/${caseId}/notices/${notice.id}/regenerate-draft`,
        { extra_instructions: instruction.trim() || null },
      );
      onUpdate(data);
      setInstruction("");
      toast.success("New draft ready.");
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setRegenerating(false);
    }
  };

  const copyDraft = async () => {
    if (!notice.draft_response) return;
    try {
      await navigator.clipboard.writeText(notice.draft_response);
      toast.success("Draft copied to your clipboard.");
    } catch {
      toast.error("Couldn't copy. Select the text manually and use Ctrl/Cmd+C.");
    }
  };

  return (
    <li
      className="card-surface p-4"
      style={{ borderColor: showDraft ? "var(--warn-border)" : "var(--border-stone)" }}
      data-testid={`notice-row-${notice.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`pill ${pillClass}`}>{TYPE_LABEL[type] || "USCIS Notice"}</span>
            {e.uscis_receipt_number && (
              <span className="pill" style={{ background: "#F3F4F6", color: "#374151" }}
                    data-testid="notice-receipt">
                <FileText className="w-3 h-3" /> {e.uscis_receipt_number}
              </span>
            )}
            {e.form_type && (
              <span className="pill" style={{ background: "#EEF2FF", color: "#3730A3" }}>
                {e.form_type}
              </span>
            )}
            {e.action_required && (
              <span className="pill pill-bad" data-testid="notice-action-required">
                <AlertTriangle className="w-3 h-3" /> Action required
              </span>
            )}
            {deadline && daysUntil != null && (
              <span
                className={`pill ${deadlinePillClass(daysUntil)}`}
                data-testid="notice-deadline"
              >
                <CalendarClock className="w-3 h-3" />
                Reply by {formatDate(deadline)} ({daysUntil} day{daysUntil === 1 ? "" : "s"} left)
              </span>
            )}
          </div>
          <div className="font-display font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>
            {e.summary || notice.filename || "USCIS notice"}
          </div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            {e.notice_date && `Dated ${formatDate(e.notice_date)}`}
            {e.petitioner_name && ` · Petitioner: ${e.petitioner_name}`}
            {e.beneficiary_name && ` · Beneficiary: ${e.beneficiary_name}`}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setOpen((v) => !v)}
                  className="btn-ghost text-xs" data-testid={`notice-toggle-${notice.id}`}>
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <button type="button" onClick={onDelete}
                  className="btn-ghost text-xs" title="Remove notice"
                  data-testid={`notice-delete-${notice.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t space-y-3"
             style={{ borderColor: "var(--border-stone)" }}>
          {/* Issues raised */}
          {e.issues && e.issues.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-wider mb-2"
                   style={{ color: "var(--text-secondary)" }}>
                Issues raised by USCIS ({e.issues.length})
              </div>
              <ul className="space-y-2" data-testid="notice-issues">
                {e.issues.map((iss, i) => (
                  <li key={`${iss.topic || "issue"}-${i}`} className="text-xs flex items-start gap-2"
                      style={{ color: "var(--text-primary)" }}>
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                      style={{
                        background: iss.severity === "critical" ? "#991B1B"
                                    : iss.severity === "minor" ? "#065F46" : "#92400E",
                      }}
                    />
                    <div>
                      <div className="font-semibold">{iss.topic}</div>
                      <div style={{ color: "var(--text-secondary)" }}>{iss.requested}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommended next steps */}
          {e.recommended_next_steps && e.recommended_next_steps.length > 0 && (
            <div className="rounded-md p-3"
                 style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}>
              <div className="text-xs font-semibold mb-1 flex items-center gap-1"
                   style={{ color: "var(--ai-text)" }}>
                <Sparkles className="w-3.5 h-3.5" /> Recommended next steps
              </div>
              <ul className="text-xs list-disc pl-4 space-y-0.5"
                  style={{ color: "var(--ai-text)" }}>
                {e.recommended_next_steps.map((s, i) => <li key={`${s}-${i}`}>{s}</li>)}
              </ul>
            </div>
          )}

          {/* AI draft response for RFEs / NOIDs */}
          {showDraft && (
            <div data-testid="notice-draft-block">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[10.5px] uppercase tracking-wider"
                     style={{ color: "var(--text-secondary)" }}>
                  AI-drafted response letter
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={copyDraft}
                          disabled={!notice.draft_response}
                          className="btn-ghost text-xs"
                          data-testid="notice-copy-draft">
                    <Copy className="w-3.5 h-3.5 inline mr-1" /> Copy
                  </button>
                </div>
              </div>
              {notice.draft_response ? (
                <div
                  className="rounded-md p-4 max-h-[420px] overflow-auto whitespace-pre-wrap font-mono text-[12px] leading-relaxed"
                  style={{ background: "#FFFBF3", border: "1px solid var(--warn-border)",
                           color: "var(--text-primary)" }}
                  data-testid="notice-draft-text"
                >
                  {notice.draft_response}
                </div>
              ) : (
                <div className="text-xs italic p-3 rounded-md"
                     style={{ background: "#FFFBF3", color: "var(--text-secondary)",
                              border: "1px solid var(--warn-border)" }}>
                  {notice.draft_error
                    ? `We couldn't generate a draft: ${notice.draft_error}. Click Regenerate below to try again.`
                    : "The draft is being prepared. If it doesn't appear in a few seconds, click Regenerate."}
                </div>
              )}

              {/* Regenerate with extra instructions */}
              <div className="mt-3">
                <label className="text-[10.5px] uppercase tracking-wider block mb-1"
                       style={{ color: "var(--text-secondary)" }}>
                  Add a note before regenerating (optional)
                </label>
                <textarea
                  className="field-input text-xs"
                  rows={2}
                  value={instruction}
                  onChange={(ev) => setInstruction(ev.target.value)}
                  placeholder="e.g. Emphasize that we already submitted marriage photos with the original petition."
                  data-testid="notice-regenerate-instruction"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={regenerate}
                    disabled={regenerating}
                    className="btn-primary text-xs"
                    data-testid="notice-regenerate-btn"
                  >
                    {regenerating
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Regenerating…</>
                      : <><RefreshCw className="w-3.5 h-3.5 inline mr-1" /> Regenerate draft</>}
                  </button>
                  <span className="text-[11px] italic" style={{ color: "var(--text-secondary)" }}>
                    Draft is AI-generated. Review with a licensed attorney before mailing.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Approval / decision cases just get a friendly acknowledgment */}
          {(type === "approval" || type === "receipt") && (
            <div className="rounded-md p-3 text-xs flex items-start gap-2"
                 style={{ background: "var(--success-bg)", color: "var(--success-text)",
                          border: "1px solid #A7F3D0" }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <span>
                Great news — this looks like a{" "}
                <strong>{type === "approval" ? "positive decision" : "receipt confirmation"}</strong>.
                Keep the original in a safe place; we've filed a copy on this case.
              </span>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

function daysBetween(from, to) {
  if (!(to instanceof Date) || isNaN(to)) return null;
  const ms = to.getTime() - from.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
